<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { AppChip, InlineNotification } from '@sunlabde/m2m-library';
import { useI18n } from 'vue-i18n';
import { useBreakpoints } from '@vueuse/core';
import { match } from 'ts-pattern';
import TransitionExpand from './TransitionExpand.vue';
import CardSegmentsWrapper from './CardSegmentsWrapper.vue';
import { getFormattedPrice } from '@/models/Currency';
import { type PlanVariant, getBillingOccurenceDescription, getLocalizedDisplayName } from '@/models/PlanVariant';
import { usePlanVariant } from '@/composables/usePlanVariant';
import { useLanguage } from '@/composables/useLanguage';
import { useCartStore } from '@/store/cart';
import type { PaymentMethod, PaymentMethodSignup } from '@/utilities/PaymentMethods';
import { useBillwerkStore } from '@/store/billwerk';
import type { CustomerChange, SecretPaymentData } from '@/models/Billwerk';
import { useProductsStore } from '@/store/products';
import { type Localization, useLocalization } from '@/models/Localization';
import { globalBreakpoints } from '@/utilities/Breakpoints';

const emit = defineEmits<{
	(e: 'selectPaymentMethod'): void;
	(e: 'change', state: boolean): void;
	(e: 'submit'): void;
	(e: 'success'): void;
	(e: 'changePlan'): void;
}>();

const { selectedLanguage } = useLanguage();

const formRef = ref<HTMLFormElement>();
const cartStore = useCartStore();

const productsStore = useProductsStore();
const { allowsTrialOrder } = storeToRefs(productsStore);

const computedPlanVariant = computed(() => cartStore.cart?.planVariant);

const getDisplayName = (planVariant: PlanVariant) => getLocalizedDisplayName(planVariant, selectedLanguage.value.code);

const appliedDiscount = computed(() => cartStore.appliedDiscount);

const isTrialOrder = computed(() => allowsTrialOrder.value);

const waitingForPayment = ref(false);

const billwerkStore = useBillwerkStore();
const { availablePaymentMethods, selectedPaymentMethod } = storeToRefs(billwerkStore);

const isPaymentMethodSelected = (paymentMethod: PaymentMethodSignup) => {
	if (selectedPaymentMethod.value) {
		return paymentMethod.ID === selectedPaymentMethod.value.ID;
	}

	return false;
};

const { localize, localizePlural } = useLocalization();
const { t } = useI18n();

const { pricePerOccurenceInCents: priceInCents, pricePerOccurenceInCentsWithMaybeDiscount: priceInCentsWithMaybeDiscount, currency } = usePlanVariant({
	discount: appliedDiscount,
	planVariant: computedPlanVariant,
});

const selectedPlanBillingOccurenceName = computed(() => {
	if (isTrialOrder.value) {
		return t('billingOccurence.trial.name');
	}

	if (computedPlanVariant.value && Object.keys(computedPlanVariant.value).length > 0) {
		return localizePlural(getBillingOccurenceDescription(computedPlanVariant.value).name, 1);
	}

	return '';
});

const pricePerOccurenceInCents = computed<number>(() => {
	if (!computedPlanVariant.value) {
		return 0;
	}

	if (isTrialOrder.value) {
		return 0;
	}

	return priceInCentsWithMaybeDiscount.value;
});

const priceBeforeDiscountInCents = computed(() => {
	if (!computedPlanVariant.value) {
		return 0;
	}

	return priceInCents.value;
});

const selectPaymentMethod = (paymentMethod: PaymentMethodSignup) => {
	if (!waitingForPayment.value) {
		billwerkStore.paymentMethod = paymentMethod.ID;
		emit('selectPaymentMethod');
	}
};

const created = async () => {
	if (!selectedPaymentMethod.value && availablePaymentMethods.value.length > 0) {
		selectPaymentMethod(availablePaymentMethods.value[0]);
	}
};

const submit = () => {
	waitingForPayment.value = true;
	formRef.value?.submit();
};

const onChange = (isValid: boolean) => {
	emit('change', isValid);
};

const onSubmit = () => {
	waitingForPayment.value = true;
	formRef.value?.submit();
	emit('submit');
};

const onSuccess = () => {
	waitingForPayment.value = false;
	emit('success');
};

onMounted(() => {
	created();
});

const onUpdateSecretPaymentData = (secretPaymentData: SecretPaymentData) => {
	billwerkStore.secretPaymentData = secretPaymentData;
};

const onUpdateCustomerChange = (customerChange: CustomerChange) => {
	billwerkStore.customerChange = customerChange;
};

const { smallerOrEqual } = useBreakpoints(globalBreakpoints);
const isSmallerOrEqualDesktop = smallerOrEqual('desktop');

const tooltipContent = (localization: Localization) => {
	if (isSmallerOrEqualDesktop.value) {
		return undefined;
	}

	return localize(localization);
};

defineExpose({ submit });

const getPaymentImageHeight = (id: PaymentMethod) => match(id)
	.with('PayPal:Reepay', () => 'h-12')
	.with('CreditCard:Adyen', () => 'h-6 mobile:h-8')
	.with('Debit:Heidelpay', () => 'h-9')
	.exhaustive();
</script>

<template>
	<CardSegmentsWrapper>
		<template #header>
			<div
				v-if="computedPlanVariant"
				class="text-center phablet:flex phablet:items-center phablet:justify-between phablet:text-left"
			>
				<p
					class="text-lg font-bold"
					data-cy="selected-planvariant-name"
				>
					{{ getDisplayName(computedPlanVariant) }}
				</p>
			</div>
		</template>
		<template #body>
			<template v-if="availablePaymentMethods.length > 0">
				<div class="leading-[1.75] mb-8">
					<p class="font-bold">
						{{ $t('subscribe.step.plan.method') }}
					</p>
				</div>
				<div class="leading-[1.75]">
					<div class="grid desktop:grid-cols-2 grid-cols-1 gap-3 mb-4">
						<div>
							<div class="flex flex-col gap-2 w-full mobile:w-fit mobile:flex-row">
								<AppChip
									v-for="paymentMethod in availablePaymentMethods"
									:key="paymentMethod.ID"
									v-tippy="{
										content: tooltipContent(paymentMethod.name),
										placement: 'bottom',
									}"
									:is-selected="isPaymentMethodSelected(paymentMethod)"
									:is-disabled="waitingForPayment"
									:data-cy="`card_paymentMethod_${paymentMethod.ID}`"
									class="w-full"
									@click="selectPaymentMethod(paymentMethod)"
								>
									<img
										v-if="paymentMethod.image"
										:src="paymentMethod.image"
										:alt="paymentMethod.image"
										class="object-contain"
										:class="getPaymentImageHeight(paymentMethod.ID)"
									>
									<span v-else>{{ paymentMethod.name }}</span>
								</AppChip>
							</div>
						</div>
						<div>
							<InlineNotification
								class="ml-auto"
								icon="lock-keyhole"
								color="green-light"
								:description="$t('sslBoxDesc')"
							/>
						</div>
					</div>
				</div>
			</template>
			<div
				v-else
				class="leading-[1.75] mb-4"
			>
				<p class="text-xl mb-4">
					{{ $t('subscribe.step.payment.noMethodAvailable') }}
				</p>
			</div>
			<div class="leading-[1.75]">
				<div
					v-if="selectedPaymentMethod"
					key="boxSection"
					class="leading-[1.75]"
				>
					<TransitionExpand mode="out-in">
						<keep-alive>
							<component
								:is="selectedPaymentMethod.view"
								ref="formRef"
								@change="onChange"
								@submit="onSubmit"
								@success="onSuccess"
								@update:secret-payment-data="onUpdateSecretPaymentData"
								@update:customer-change="onUpdateCustomerChange"
							/>
						</keep-alive>
					</TransitionExpand>
				</div>
			</div>
		</template>
		<template #footer>
			<div
				v-if="computedPlanVariant"
				class="flex justify-between"
			>
				<div class="footer-left">
					<i18n-t
						keypath="price.totalWithBillingOccurence"
						scope="global"
					>
						<template #billingOccurence>
							{{ selectedPlanBillingOccurenceName }}
						</template>
					</i18n-t>
				</div>
				<div class="footer-right">
					<p
						v-if="currency"
						class="font-bold whitespace-nowrap"
					>
						<span
							v-if="priceBeforeDiscountInCents && priceBeforeDiscountInCents > pricePerOccurenceInCents"
							class="font-normal line-through mr-2"
						>
							{{ getFormattedPrice(priceBeforeDiscountInCents, currency, { fromCents: true }) }}
						</span>
						<span>
							{{ getFormattedPrice(pricePerOccurenceInCents, currency, { fromCents: true }) }}
						</span>
					</p>
				</div>
			</div>
		</template>
	</CardSegmentsWrapper>
</template>
